import { Dialog, Transition } from '@headlessui/react';
import { Button, Stack } from '@mui/joy';
import React, { ReactNode, Fragment } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  children: ReactNode;
  title: string;
  isOpen: boolean;
  setOpen: Function;
  showCancel: boolean;
  onConfirm: () => Promise<boolean>;
  confirmText?: string;
  disabled?: boolean;
};

function Modal(props: Props) {
  const {
    children,
    title,
    isOpen,
    setOpen,
    showCancel,
    onConfirm,
    confirmText,
  } = props;
  const { t } = useTranslation();

  const close = () => setOpen(false);

  const confirm = async () => {
    if (onConfirm) {
      const success = await onConfirm();
      if (success) {
        close();
      }
    } else {
      close();
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={close}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-gray-900"
                >
                  {title}
                </Dialog.Title>
                <div className="mt-2">{children}</div>

                <Stack spacing={1} direction={'row'} justifyContent={'end'}>
                  {showCancel && (
                    <Button
                      onClick={close}
                      disabled={props.disabled}
                      variant="soft"
                    >
                      {t('courses.button_cancel')}
                    </Button>
                  )}
                  <Button onClick={confirm} disabled={props.disabled}>
                    {confirmText ? confirmText : t('courses.buttom_submit')}
                  </Button>
                </Stack>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export default Modal;
